import { generateId } from './helpers';

const NODE_WIDTH  = 280;
const NODE_HEIGHT = 200;
const COL_GAP     = 60;
const ROW_GAP     = 60;
const COLS        = 3;

/**
 * Extract valid JSON from a string that may contain markdown fences.
 */
function extractJson(text) {
  // Try to strip ```json ... ``` or ``` ... ``` wrappers
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  const raw = fenced ? fenced[1] : text;
  return JSON.parse(raw.trim());
}

/**
 * Calculate canvas positions for new nodes, placing them below existing ones.
 */
function calculatePositions(newNodes, existingNodes) {
  if (existingNodes.length === 0) {
    return newNodes.map((_, i) => ({
      x: 80 + (i % COLS) * (NODE_WIDTH + COL_GAP),
      y: 80 + Math.floor(i / COLS) * (NODE_HEIGHT + ROW_GAP),
    }));
  }

  const maxY = Math.max(...existingNodes.map(n => (n.position?.y ?? 0) + NODE_HEIGHT));
  const avgX = existingNodes.reduce((s, n) => s + (n.position?.x ?? 0), 0) / existingNodes.length;
  const startX = Math.max(80, avgX - (Math.min(newNodes.length, COLS) / 2) * (NODE_WIDTH + COL_GAP));

  return newNodes.map((_, i) => ({
    x: startX + (i % COLS) * (NODE_WIDTH + COL_GAP),
    y: maxY + ROW_GAP + Math.floor(i / COLS) * (NODE_HEIGHT + ROW_GAP),
  }));
}

/**
 * Convert a field object from the LLM response to the internal field format.
 */
function normalizeField(f) {
  return {
    name:         f.name        || '',
    type:         f.type        || 'VARCHAR',
    isPrimaryKey: !!f.isPrimaryKey,
    isForeignKey: !!f.isForeignKey,
    autoIncrement:!!f.autoIncrement,
    nullable:     f.nullable !== undefined ? f.nullable : !f.isPrimaryKey,
    description:  f.description || '',
    checkConstraint: f.checkConstraint || f.check || '',
    constraints:  [],
  };
}

/**
 * Parse and enrich the LLM response (changesJson) so it can be directly
 * consumed by useFlowStore.applyAiChanges().
 *
 * @param {object|string} raw          - changesJson from the backend response
 * @param {Array}         existingNodes - current nodes in the store
 * @returns {{
 *   newNodes:      Array,  // nodes with id + position, ready to push
 *   modifiedNodes: Array,  // { id, addFields }
 *   newEdges:      Array,  // edges with source/target as node IDs
 *   summary:       string
 * }}
 */
export function parseAiResponse(raw, existingNodes) {
  let parsed;
  if (typeof raw === 'string') {
    parsed = extractJson(raw);
  } else {
    parsed = raw;
  }

  const summary       = parsed?.summary || '';
  const rawNewNodes   = Array.isArray(parsed?.newNodes)       ? parsed.newNodes       : [];
  const modifiedNodes = Array.isArray(parsed?.modifiedNodes)  ? parsed.modifiedNodes  : [];
  const rawNewEdges   = Array.isArray(parsed?.newEdges)       ? parsed.newEdges       : [];

  // Validate we have at least one operation
  if (!rawNewNodes.length && !modifiedNodes.length && !rawNewEdges.length) {
    throw new Error('The model returned no changes. Try rephrasing your request.');
  }

  // Build new nodes with IDs and positions
  const positions = calculatePositions(rawNewNodes, existingNodes);
  const newNodes = rawNewNodes.map((n, i) => ({
    id:       generateId(),
    type:     n.type || 'table',
    position: positions[i],
    data: {
      name:        n.name        || 'New Entity',
      description: n.description || '',
      fields:      (n.fields || []).map(normalizeField),
      tags:        n.tags        || [],
      color:       n.color       || null,
      ...(n.sqlDefinition ? { sqlDefinition: n.sqlDefinition } : {}),
    },
  }));

  // Normalize modifiedNodes fields
  const normalizedModified = modifiedNodes.map(m => ({
    id:        m.id,
    addFields: (m.addFields || []).map(normalizeField),
  }));

  // Build name-to-id lookup: existing + just-created nodes
  const nameToId = {};
  for (const n of existingNodes) {
    if (n.data?.name) nameToId[n.data.name.toLowerCase()] = n.id;
  }
  for (const n of newNodes) {
    if (n.data?.name) nameToId[n.data.name.toLowerCase()] = n.id;
  }

  // Resolve edge references from entity names to IDs
  const newEdges = rawNewEdges
    .map(e => {
      const sourceId = nameToId[(e.sourceNodeName || '').toLowerCase()];
      const targetId = nameToId[(e.targetNodeName || '').toLowerCase()];
      if (!sourceId || !targetId) return null; // skip unresolvable edges
      return {
        id:          generateId(),
        source:      sourceId,
        target:      targetId,
        type:        'default',
        data: {
          cardinality:      e.cardinality || '1:N',
          label:            e.label       || '',
          cardinalityLocked: true,
        },
      };
    })
    .filter(Boolean);

  return { newNodes, modifiedNodes: normalizedModified, newEdges, summary };
}
